import graphql__6b622a97cb507556eff3 from "./__generated__/AnalysisForecastPlannedItemsViewQuery.graphql.ts";
import graphql__18f854e0151c8caf02a0 from "./__generated__/AnalysisForecastPlannedItemsViewEnableMutation.graphql.ts";
import graphql__52fec0bbb46248b1acd3 from "./__generated__/AnalysisForecastPlannedItemsViewDisableMutation.graphql.ts";import * as React from "react";
import {startTransition, useState} from "react";
import {useOutletContext} from "react-router";
import {graphql} from "react-relay";
import {usePreloadedQuery, useQueryLoader, useRelayEnvironment} from "react-relay/hooks";
import {useIntl} from "react-intl";
import {faPencil, faPlusCircle, faTrash} from "@fortawesome/pro-regular-svg-icons";
import Panel from "./presentational/Panel";
import Table from "./presentational/table/Table";
import TableHeader, {TextAlignment} from "./presentational/table/TableHeader";
import TableData from "./presentational/table/TableData";
import ActionButton from "./presentational/button/ActionButton";
import EditPlannedItemDialog from "./dialog/EditPlannedItemDialog";
import DeletePlannedItemDialog from "./dialog/DeletePlannedItemDialog";
import EditSubscriptionAmountOverrideDialog from "./dialog/EditSubscriptionAmountOverrideDialog";
import RemoveSubscriptionAmountOverrideDialog from "./dialog/RemoveSubscriptionAmountOverrideDialog";
import EditSupplierAmountOverrideDialog from "./dialog/EditSupplierAmountOverrideDialog";
import RemoveSupplierAmountOverrideDialog from "./dialog/RemoveSupplierAmountOverrideDialog";
import {commitMutationWithErrorHandling} from "../helper/commitMutationWithErrorHandling";
import {formatAmount} from "../helper/revenueFormat";
import {useErrorContext} from "../contexts/ErrorContext";
import {vatRatePercent} from "../helper/vatRate";
import AnalysisForecastPlannedItemsViewQueryNode from "./__generated__/AnalysisForecastPlannedItemsViewQuery.graphql";
import type {AnalysisForecastPlannedItemsViewQuery as AnalysisForecastPlannedItemsViewQueryType} from "./__generated__/AnalysisForecastPlannedItemsViewQuery.graphql";
import type {AnalysisForecastPlannedItemsViewEnableMutation} from "./__generated__/AnalysisForecastPlannedItemsViewEnableMutation.graphql";
import type {AnalysisForecastPlannedItemsViewDisableMutation} from "./__generated__/AnalysisForecastPlannedItemsViewDisableMutation.graphql";
import {formatDay} from "../helper/dateFormat";

const analysisForecastPlannedItemsViewQuery = graphql__6b622a97cb507556eff3;

const enableMutation = graphql__18f854e0151c8caf02a0;

const disableMutation = graphql__52fec0bbb46248b1acd3;

type AnalysisOutletContext = {
    plannedItemsQuery: any
};

type PlannedItemRow = AnalysisForecastPlannedItemsViewQueryType['response']['plannedItems'][number];
type OverrideRow = AnalysisForecastPlannedItemsViewQueryType['response']['subscriptionAmountOverrides'][number];
type SupplierOverrideRow = AnalysisForecastPlannedItemsViewQueryType['response']['supplierAmountOverrides'][number];

type OpenDialog =
    | {type: 'addItem'}
    | {type: 'editItem', plannedItem: PlannedItemRow}
    | {type: 'deleteItem', plannedItem: PlannedItemRow}
    | {type: 'addOverride'}
    | {type: 'editOverride', override: OverrideRow}
    | {type: 'removeOverride', override: OverrideRow}
    | {type: 'addSupplierOverride'}
    | {type: 'editSupplierOverride', override: SupplierOverrideRow}
    | {type: 'removeSupplierOverride', override: SupplierOverrideRow}
    | null;

const categoryColors: Record<string, string> = {
    REVENUE: 'bg-green-100 text-green-800',
    OTHER_OPERATING_INCOME: 'bg-green-100 text-green-800',
    SUPPLIERS: 'bg-blue-100 text-blue-800',
    PERSONNEL: 'bg-purple-100 text-purple-800',
    DEPRECIATION: 'bg-gray-100 text-gray-800',
    INCOME_TAXES: 'bg-yellow-100 text-yellow-800',
    FINANCING: 'bg-indigo-100 text-indigo-800',
    OTHER: 'bg-red-100 text-red-800',
};

/**
 * The month after the current one: planned items are about the future.
 */
const nextCalendarMonth = (): string => {
    const today = new Date();
    const next = new Date(Date.UTC(today.getFullYear(), today.getMonth() + 1, 1));
    return next.getUTCFullYear() + '-' + String(next.getUTCMonth() + 1).padStart(2, '0');
};

/**
 * Lists the planned items, which can be switched off to try out scenarios, and the amounts that
 * replace the monthly amount of a subscription in the income forecast or the amount of the last
 * invoices of a supplier in the expense forecast.
 */
const AnalysisForecastPlannedItemsView: React.FC = () => {
    const intl = useIntl();
    const relayEnvironment = useRelayEnvironment();
    const {addError} = useErrorContext();
    const {plannedItemsQuery} = useOutletContext<AnalysisOutletContext>();
    const [queryRef, loadPlannedItemsQuery] = useQueryLoader<AnalysisForecastPlannedItemsViewQueryType>(AnalysisForecastPlannedItemsViewQueryNode, plannedItemsQuery);
    const data = usePreloadedQuery<AnalysisForecastPlannedItemsViewQueryType>(analysisForecastPlannedItemsViewQuery, queryRef ?? plannedItemsQuery);
    const [openDialog, setOpenDialog] = useState<OpenDialog>(null);

    const plannedItems = data.plannedItems;
    const overrides = data.subscriptionAmountOverrides;
    const supplierOverrides = data.supplierAmountOverrides;

    const reload = () => {
        startTransition(() => {
            loadPlannedItemsQuery({}, {fetchPolicy: 'network-only'});
        });
    };

    const closeDialog = (changed: boolean) => {
        setOpenDialog(null);
        if (changed) {
            reload();
        }
    };

    const toggleEnabled = (plannedItem: PlannedItemRow) => {
        const options = {
            variables: {input: {id: plannedItem.id}},
            onCompleted: reload,
            onError: reload,
        };
        const customErrorMessage = intl.formatMessage({id: 'errors.failedToSwitchPlannedItem'});
        if (plannedItem.enabled) {
            commitMutationWithErrorHandling<AnalysisForecastPlannedItemsViewDisableMutation>(relayEnvironment, {mutation: disableMutation, ...options}, addError, {customErrorMessage});
        } else {
            commitMutationWithErrorHandling<AnalysisForecastPlannedItemsViewEnableMutation>(relayEnvironment, {mutation: enableMutation, ...options}, addError, {customErrorMessage});
        }
    };

    const formatMonth = (month: string) => intl.formatDate(month + '-01', {month: 'short', year: 'numeric', timeZone: 'UTC'});
    const describeSubscription = (override: OverrideRow) => override.subscription
        ? {customerName: override.subscription.customerAccount.customerName, label: override.subscription.subscriptionServiceReferenceLabel ?? override.id}
        : {customerName: override.id, label: ''};

    return (
        <>
            <Panel
                headline={intl.formatMessage({id: 'view.analysisForecastPlannedItems.headline'})}
                buttons={
                    <ActionButton
                        icon={faPlusCircle}
                        title={intl.formatMessage({id: 'view.analysisForecastPlannedItems.addButton'})}
                        onClick={() => setOpenDialog({type: 'addItem'})}
                    />
                }
                fullWidthChildren={true}
            >
                <p className="px-4 py-3 sm:px-6 text-sm text-gray-500 border-b border-gray-200">
                    {intl.formatMessage({id: 'view.analysisForecastPlannedItems.description'})}
                </p>
                {plannedItems.length === 0 ? (
                    <p className="px-4 py-5 sm:p-6 text-sm text-gray-500">{intl.formatMessage({id: 'view.analysisForecastPlannedItems.empty'})}</p>
                ) : (
                    <div className="overflow-x-auto">
                        <Table>
                            <thead>
                                <tr>
                                    <TableHeader className="pl-4 sm:pl-6 pr-3">{intl.formatMessage({id: 'view.analysisForecastPlannedItems.col.enabled'})}</TableHeader>
                                    <TableHeader className="px-3">{intl.formatMessage({id: 'view.analysisForecastPlannedItems.col.name'})}</TableHeader>
                                    <TableHeader className="px-3">{intl.formatMessage({id: 'view.analysisForecastPlannedItems.col.rhythm'})}</TableHeader>
                                    <TableHeader className="px-3">{intl.formatMessage({id: 'view.analysisForecastPlannedItems.col.nextDueDate'})}</TableHeader>
                                    <TableHeader className="px-3" textAlignment={TextAlignment.Right}>{intl.formatMessage({id: 'view.analysisForecastPlannedItems.col.netAmount'})}</TableHeader>
                                    <TableHeader className="pl-3 pr-4 sm:pr-6"/>
                                </tr>
                            </thead>
                            <tbody>
                                {plannedItems.map((plannedItem, index) => (
                                    <tr key={plannedItem.id} className={(index % 2 === 0 ? 'bg-white' : 'bg-gray-50') + ' hover:bg-gray-100 group'}>
                                        <TableData className="pl-4 sm:pl-6 pr-3">
                                            <input
                                                type="checkbox"
                                                checked={plannedItem.enabled}
                                                onChange={() => toggleEnabled(plannedItem)}
                                                title={intl.formatMessage({id: plannedItem.enabled ? 'view.analysisForecastPlannedItems.action.disable' : 'view.analysisForecastPlannedItems.action.enable'})}
                                                className="focus:ring-red-500 h-4 w-4 text-red-600 border-gray-300 rounded-sm"
                                            />
                                        </TableData>
                                        <TableData className={'px-3' + (plannedItem.enabled ? '' : ' opacity-50')}>
                                            <div className="font-medium text-gray-900">{plannedItem.definition.name}</div>
                                            <div className="mt-0.5 space-x-2">
                                                <span className={'inline-flex rounded-full px-2 py-0.5 text-xs font-medium ' + (categoryColors[plannedItem.definition.category] ?? 'bg-gray-100 text-gray-800')}>
                                                    {intl.formatMessage({id: 'forecastCategory.' + plannedItem.definition.category})}
                                                </span>
                                                {plannedItem.definition.effect !== 'RESULT_AND_LIQUIDITY' && (
                                                    <span className="text-xs text-gray-500">{intl.formatMessage({id: 'plannedItemEffect.' + plannedItem.definition.effect})}</span>
                                                )}
                                                {plannedItem.definition.liquidityGroup && (
                                                    <span className="text-xs text-gray-500">{intl.formatMessage({id: 'view.analysisForecastPlannedItems.liquidityGroup'}, {group: intl.formatMessage({id: 'bankTransactionGroup.' + plannedItem.definition.liquidityGroup})})}</span>
                                                )}
                                            </div>
                                        </TableData>
                                        <TableData className={'px-3 whitespace-nowrap' + (plannedItem.enabled ? '' : ' opacity-50')}>
                                            <div>
                                                {intl.formatMessage({id: 'plannedItemRhythm.' + plannedItem.definition.rhythm})}
                                                {', '}
                                                {intl.formatMessage({id: 'view.analysisForecastPlannedItems.dayOfMonth'}, {day: plannedItem.definition.dayOfMonth})}
                                            </div>
                                            <div className="text-xs text-gray-500">
                                                {plannedItem.definition.endMonth
                                                    ? intl.formatMessage({id: 'view.analysisForecastPlannedItems.period'}, {start: formatMonth(plannedItem.definition.startMonth), end: formatMonth(plannedItem.definition.endMonth)})
                                                    : intl.formatMessage({id: plannedItem.definition.rhythm === 'ONE_TIME' ? 'view.analysisForecastPlannedItems.in' : 'view.analysisForecastPlannedItems.from'}, {start: formatMonth(plannedItem.definition.startMonth)})}
                                            </div>
                                        </TableData>
                                        <TableData className={'px-3 whitespace-nowrap' + (plannedItem.enabled ? '' : ' opacity-50')}>
                                            {plannedItem.nextDueDate ? formatDay(intl, plannedItem.nextDueDate) : <span className="text-gray-300">–</span>}
                                        </TableData>
                                        <TableData className={'px-3 whitespace-nowrap' + (plannedItem.enabled ? '' : ' opacity-50')} textAlignment={TextAlignment.Right}>
                                            <div className="font-medium">{formatAmount(plannedItem.definition.netAmount)}</div>
                                            {plannedItem.definition.vatRate !== 'NONE' && (
                                                <div className="text-xs text-gray-500">
                                                    {intl.formatMessage({id: 'view.analysisForecastPlannedItems.plusVat'}, {rate: vatRatePercent(plannedItem.definition.vatRate)})}
                                                </div>
                                            )}
                                        </TableData>
                                        <TableData className="pl-3 pr-4 sm:pr-6 whitespace-nowrap" textAlignment={TextAlignment.Right}>
                                            <ActionButton
                                                icon={faPencil}
                                                title={intl.formatMessage({id: 'view.analysisForecastPlannedItems.action.edit'})}
                                                additionalClassname="invisible group-hover:visible"
                                                onClick={() => setOpenDialog({type: 'editItem', plannedItem})}
                                            />
                                            <ActionButton
                                                icon={faTrash}
                                                title={intl.formatMessage({id: 'view.analysisForecastPlannedItems.action.delete'})}
                                                additionalClassname="invisible group-hover:visible"
                                                onClick={() => setOpenDialog({type: 'deleteItem', plannedItem})}
                                            />
                                        </TableData>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                )}
            </Panel>

            <div className="mt-6">
                <Panel
                    headline={intl.formatMessage({id: 'view.analysisForecastPlannedItems.overrides.headline'})}
                    buttons={
                        <ActionButton
                            icon={faPlusCircle}
                            title={intl.formatMessage({id: 'view.analysisForecastPlannedItems.overrides.addButton'})}
                            onClick={() => setOpenDialog({type: 'addOverride'})}
                        />
                    }
                    fullWidthChildren={true}
                >
                    <p className="px-4 py-3 sm:px-6 text-sm text-gray-500 border-b border-gray-200">
                        {intl.formatMessage({id: 'view.analysisForecastPlannedItems.overrides.description'})}
                    </p>
                    {overrides.length === 0 ? (
                        <p className="px-4 py-5 sm:p-6 text-sm text-gray-500">{intl.formatMessage({id: 'view.analysisForecastPlannedItems.overrides.empty'})}</p>
                    ) : (
                        <div className="overflow-x-auto">
                            <Table>
                                <thead>
                                    <tr>
                                        <TableHeader className="pl-4 sm:pl-6 pr-3">{intl.formatMessage({id: 'view.analysisForecastPlannedItems.overrides.col.subscription'})}</TableHeader>
                                        <TableHeader className="px-3" textAlignment={TextAlignment.Right}>{intl.formatMessage({id: 'view.analysisForecastPlannedItems.overrides.col.monthlyNetAmount'})}</TableHeader>
                                        <TableHeader className="px-3">{intl.formatMessage({id: 'view.analysisForecastPlannedItems.overrides.col.note'})}</TableHeader>
                                        <TableHeader className="px-3">{intl.formatMessage({id: 'view.analysisForecastPlannedItems.overrides.col.setBy'})}</TableHeader>
                                        <TableHeader className="pl-3 pr-4 sm:pr-6"/>
                                    </tr>
                                </thead>
                                <tbody>
                                    {overrides.map((override, index) => (
                                        <tr key={override.id} className={(index % 2 === 0 ? 'bg-white' : 'bg-gray-50') + ' hover:bg-gray-100 group'}>
                                            <TableData className="pl-4 sm:pl-6 pr-3">
                                                <div className="font-medium text-gray-900">{describeSubscription(override).customerName}</div>
                                                <div className="text-xs text-gray-500">{describeSubscription(override).label}</div>
                                            </TableData>
                                            <TableData className="px-3 whitespace-nowrap font-medium" textAlignment={TextAlignment.Right}>{formatAmount(override.monthlyNetAmount)}</TableData>
                                            <TableData className="px-3">{override.note ?? <span className="text-gray-300">–</span>}</TableData>
                                            <TableData className="px-3 text-xs text-gray-500">
                                                {intl.formatMessage({id: 'view.analysisForecastPlannedItems.overrides.setBy'}, {user: override.setBy, date: intl.formatDate(override.setAt, {dateStyle: 'medium'})})}
                                            </TableData>
                                            <TableData className="pl-3 pr-4 sm:pr-6 whitespace-nowrap" textAlignment={TextAlignment.Right}>
                                                <ActionButton
                                                    icon={faPencil}
                                                    title={intl.formatMessage({id: 'view.analysisForecastPlannedItems.overrides.action.edit'})}
                                                    additionalClassname="invisible group-hover:visible"
                                                    onClick={() => setOpenDialog({type: 'editOverride', override})}
                                                />
                                                <ActionButton
                                                    icon={faTrash}
                                                    title={intl.formatMessage({id: 'view.analysisForecastPlannedItems.overrides.action.remove'})}
                                                    additionalClassname="invisible group-hover:visible"
                                                    onClick={() => setOpenDialog({type: 'removeOverride', override})}
                                                />
                                            </TableData>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    )}
                </Panel>
            </div>

            <div className="mt-6">
                <Panel
                    headline={intl.formatMessage({id: 'view.analysisForecastPlannedItems.supplierOverrides.headline'})}
                    buttons={
                        <ActionButton
                            icon={faPlusCircle}
                            title={intl.formatMessage({id: 'view.analysisForecastPlannedItems.supplierOverrides.addButton'})}
                            onClick={() => setOpenDialog({type: 'addSupplierOverride'})}
                        />
                    }
                    fullWidthChildren={true}
                >
                    <p className="px-4 py-3 sm:px-6 text-sm text-gray-500 border-b border-gray-200">
                        {intl.formatMessage({id: 'view.analysisForecastPlannedItems.supplierOverrides.description'})}
                    </p>
                    {supplierOverrides.length === 0 ? (
                        <p className="px-4 py-5 sm:p-6 text-sm text-gray-500">{intl.formatMessage({id: 'view.analysisForecastPlannedItems.overrides.empty'})}</p>
                    ) : (
                        <div className="overflow-x-auto">
                            <Table>
                                <thead>
                                    <tr>
                                        <TableHeader className="pl-4 sm:pl-6 pr-3">{intl.formatMessage({id: 'view.analysisForecastPlannedItems.supplierOverrides.col.supplier'})}</TableHeader>
                                        <TableHeader className="px-3" textAlignment={TextAlignment.Right}>{intl.formatMessage({id: 'view.analysisForecastPlannedItems.supplierOverrides.col.netAmount'})}</TableHeader>
                                        <TableHeader className="px-3">{intl.formatMessage({id: 'view.analysisForecastPlannedItems.overrides.col.note'})}</TableHeader>
                                        <TableHeader className="px-3">{intl.formatMessage({id: 'view.analysisForecastPlannedItems.overrides.col.setBy'})}</TableHeader>
                                        <TableHeader className="pl-3 pr-4 sm:pr-6"/>
                                    </tr>
                                </thead>
                                <tbody>
                                    {supplierOverrides.map((override, index) => (
                                        <tr key={override.id} className={(index % 2 === 0 ? 'bg-white' : 'bg-gray-50') + ' hover:bg-gray-100 group'}>
                                            <TableData className="pl-4 sm:pl-6 pr-3">
                                                <div className="font-medium text-gray-900">{override.supplier?.name ?? override.id}</div>
                                                {override.supplier?.billingCycle && (
                                                    <div className="text-xs text-gray-500">{intl.formatMessage({id: 'expense.billingCycle.' + override.supplier.billingCycle})}</div>
                                                )}
                                            </TableData>
                                            <TableData className="px-3 whitespace-nowrap font-medium" textAlignment={TextAlignment.Right}>{formatAmount(override.netAmount)}</TableData>
                                            <TableData className="px-3">{override.note ?? <span className="text-gray-300">–</span>}</TableData>
                                            <TableData className="px-3 text-xs text-gray-500">
                                                {intl.formatMessage({id: 'view.analysisForecastPlannedItems.overrides.setBy'}, {user: override.setBy, date: intl.formatDate(override.setAt, {dateStyle: 'medium'})})}
                                            </TableData>
                                            <TableData className="pl-3 pr-4 sm:pr-6 whitespace-nowrap" textAlignment={TextAlignment.Right}>
                                                <ActionButton
                                                    icon={faPencil}
                                                    title={intl.formatMessage({id: 'view.analysisForecastPlannedItems.overrides.action.edit'})}
                                                    additionalClassname="invisible group-hover:visible"
                                                    onClick={() => setOpenDialog({type: 'editSupplierOverride', override})}
                                                />
                                                <ActionButton
                                                    icon={faTrash}
                                                    title={intl.formatMessage({id: 'view.analysisForecastPlannedItems.overrides.action.remove'})}
                                                    additionalClassname="invisible group-hover:visible"
                                                    onClick={() => setOpenDialog({type: 'removeSupplierOverride', override})}
                                                />
                                            </TableData>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    )}
                </Panel>
            </div>

            {openDialog?.type === 'addItem' && (
                <EditPlannedItemDialog defaultStartMonth={nextCalendarMonth()} onClose={closeDialog}/>
            )}
            {openDialog?.type === 'editItem' && (
                <EditPlannedItemDialog plannedItem={openDialog.plannedItem} defaultStartMonth={nextCalendarMonth()} onClose={closeDialog}/>
            )}
            {openDialog?.type === 'deleteItem' && (
                <DeletePlannedItemDialog plannedItem={openDialog.plannedItem} onClose={closeDialog}/>
            )}
            {openDialog?.type === 'addOverride' && (
                <EditSubscriptionAmountOverrideDialog onClose={closeDialog}/>
            )}
            {openDialog?.type === 'editOverride' && (
                <EditSubscriptionAmountOverrideDialog override={openDialog.override} onClose={closeDialog}/>
            )}
            {openDialog?.type === 'removeOverride' && (
                <RemoveSubscriptionAmountOverrideDialog override={openDialog.override} onClose={closeDialog}/>
            )}
            {openDialog?.type === 'addSupplierOverride' && (
                <EditSupplierAmountOverrideDialog onClose={closeDialog}/>
            )}
            {openDialog?.type === 'editSupplierOverride' && (
                <EditSupplierAmountOverrideDialog override={openDialog.override} onClose={closeDialog}/>
            )}
            {openDialog?.type === 'removeSupplierOverride' && (
                <RemoveSupplierAmountOverrideDialog override={openDialog.override} onClose={closeDialog}/>
            )}
        </>
    );
};

export default AnalysisForecastPlannedItemsView;
